'use client';

// ============================================================================
// SOFTWARE DEVELOPMENT ANALYTICS
// Comprehensive analytics dashboard with velocity, burndown, cycle time,
// cumulative flow, and team performance metrics - comparable to Jira/ClickUp
// Date: November 9, 2025
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  Clock,
  Target,
  Users,
  Zap,
  Calendar,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Download
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { authService } from '@/lib/authService';
import { createLogger } from '@/lib/debugLogger';

const logger = createLogger('AnalyticsView');

interface AnalyticsViewProps {
  permissions: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_admin: boolean;
  };
}

interface VelocityData {
  sprint: string;
  planned: number;
  completed: number;
}

interface CumulativeFlowData {
  date: string;
  backlog: number;
  todo: number;
  inProgress: number;
  review: number;
  done: number;
}

interface CycleTimeData {
  itemType: string;
  avgCycleTime: number; // in days
  avgLeadTime: number;
}

interface TeamPerformance {
  member: string;
  completed: number;
  velocity: number;
}

export default function AnalyticsView({ permissions }: AnalyticsViewProps) {
  // State
  const [loading, setLoading] = useState(true);
  const [velocityData, setVelocityData] = useState<VelocityData[]>([]);
  const [cumulativeFlowData, setCumulativeFlowData] = useState<CumulativeFlowData[]>([]);
  const [cycleTimeData, setCycleTimeData] = useState<CycleTimeData[]>([]);
  const [teamPerformance, setTeamPerformance] = useState<TeamPerformance[]>([]);
  const [timeRange, setTimeRange] = useState<'30d' | '90d' | '6m' | '1y'>('90d');
  
  // Summary Stats
  const [stats, setStats] = useState({
    avgVelocity: 0,
    velocityTrend: 0,
    avgCycleTime: 0,
    avgLeadTime: 0,
    completionRate: 0,
    totalCompleted: 0,
    activeSprints: 0,
    teamSize: 0
  });

  const loadAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      const token = authService.getTokens()?.access_token;
      
      if (!token) {
        throw new Error('Authentication required');
      }

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      switch (timeRange) {
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
        case '6m':
          startDate.setMonth(startDate.getMonth() - 6);
          break;
        case '1y':
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
      }

      // Load sprint velocity data
      const sprintsResponse = await fetch(
        `/api/v1/work-items?item_type=sprint&module_context=software-development&start_date_after=${startDate.toISOString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (sprintsResponse.ok) {
        const sprintsData = await sprintsResponse.json();
        const sprints = sprintsData.items || sprintsData.data || [];
        
        const velocity: VelocityData[] = sprints.map((sprint: any) => ({
          sprint: sprint.title.replace(/Sprint \d+ - /, '').substring(0, 10),
          planned: sprint.custom_fields?.total_story_points || 0,
          completed: sprint.custom_fields?.completed_story_points || 0
        }));
        
        setVelocityData(velocity);

        // Calculate average velocity
        const avgVelocity = velocity.length > 0
          ? velocity.reduce((sum, v) => sum + v.completed, 0) / velocity.length
          : 0;
        
        // Calculate velocity trend (comparing last 2 sprints)
        const velocityTrend = velocity.length >= 2
          ? ((velocity[velocity.length - 1].completed - velocity[velocity.length - 2].completed) / velocity[velocity.length - 2].completed) * 100
          : 0;

        setStats(prev => ({
          ...prev,
          avgVelocity: Math.round(avgVelocity),
          velocityTrend: Math.round(velocityTrend),
          activeSprints: sprints.filter((s: any) => s.status === 'in_progress' || s.status === 'in-progress').length
        }));
      }

      // Load all work items for cumulative flow and cycle time
      const itemsResponse = await fetch(
        `/api/v1/work-items?module_context=software-development&created_after=${startDate.toISOString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (itemsResponse.ok) {
        const itemsData = await itemsResponse.json();
        const items = itemsData.items || itemsData.data || [];

        // Generate cumulative flow data
        const flowData = generateCumulativeFlowData(items, startDate, endDate);
        setCumulativeFlowData(flowData);

        // Calculate cycle time by type
        const cycleTime = calculateCycleTime(items);
        setCycleTimeData(cycleTime);

        // Calculate team performance
        const teamPerf = calculateTeamPerformance(items);
        setTeamPerformance(teamPerf);

        // Calculate summary stats
        const completedItems = items.filter((i: any) => 
          i.status === 'completed' || i.status === 'closed' || i.status === 'done'
        );

        const avgCycleTime = cycleTime.length > 0
          ? cycleTime.reduce((sum, c) => sum + c.avgCycleTime, 0) / cycleTime.length
          : 0;

        const avgLeadTime = cycleTime.length > 0
          ? cycleTime.reduce((sum, c) => sum + c.avgLeadTime, 0) / cycleTime.length
          : 0;

        const completionRate = items.length > 0
          ? (completedItems.length / items.length) * 100
          : 0;

        setStats(prev => ({
          ...prev,
          avgCycleTime: Math.round(avgCycleTime * 10) / 10,
          avgLeadTime: Math.round(avgLeadTime * 10) / 10,
          completionRate: Math.round(completionRate),
          totalCompleted: completedItems.length,
          teamSize: teamPerf.length
        }));
      }

    } catch (error) {
      logger.error('Failed to load analytics data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange, loadAnalyticsData]);

  function generateCumulativeFlowData(items: any[], startDate: Date, endDate: Date): CumulativeFlowData[] {
    const data: CumulativeFlowData[] = [];
    const dayInMs = 24 * 60 * 60 * 1000;
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / dayInMs);
    
    // Sample every week for performance
    const sampleInterval = 7;
    
    for (let i = 0; i <= totalDays; i += sampleInterval) {
      const currentDate = new Date(startDate.getTime() + i * dayInMs);
      
      const counts = {
        backlog: 0,
        todo: 0,
        inProgress: 0,
        review: 0,
        done: 0
      };

      items.forEach((item: any) => {
        const createdDate = new Date(item.created_at);
        if (createdDate > currentDate) return;

        const completedDate = item.completed_at ? new Date(item.completed_at) : null;
        if (completedDate && completedDate <= currentDate) {
          counts.done++;
        } else if (item.status === 'in_progress' || item.status === 'in-progress') {
          counts.inProgress++;
        } else if (item.status === 'review' || item.status === 'in_review') {
          counts.review++;
        } else if (item.status === 'todo' || item.status === 'open') {
          counts.todo++;
        } else {
          counts.backlog++;
        }
      });

      data.push({
        date: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        ...counts
      });
    }

    return data;
  }

  function calculateCycleTime(items: any[]): CycleTimeData[] {
    const byType: Record<string, { cycleTime: number[], leadTime: number[], count: number }> = {};

    items.forEach((item: any) => {
      if (!item.completed_at) return;

      const type = item.item_type;
      if (!byType[type]) {
        byType[type] = { cycleTime: [], leadTime: [], count: 0 };
      }

      const created = new Date(item.created_at).getTime();
      const completed = new Date(item.completed_at).getTime();
      const started = item.custom_fields?.started_at 
        ? new Date(item.custom_fields.started_at).getTime()
        : created;

      const leadTime = (completed - created) / (1000 * 60 * 60 * 24); // days
      const cycleTime = (completed - started) / (1000 * 60 * 60 * 24); // days

      byType[type].leadTime.push(leadTime);
      byType[type].cycleTime.push(cycleTime);
      byType[type].count++;
    });

    return Object.entries(byType).map(([itemType, data]) => ({
      itemType: itemType.charAt(0).toUpperCase() + itemType.slice(1),
      avgCycleTime: data.cycleTime.reduce((a, b) => a + b, 0) / data.count,
      avgLeadTime: data.leadTime.reduce((a, b) => a + b, 0) / data.count
    }));
  }

  function calculateTeamPerformance(items: any[]): TeamPerformance[] {
    const byMember: Record<string, { completed: number; velocity: number }> = {};

    items.forEach((item: any) => {
      if (!item.assigned_to) return;

      const memberId = item.assigned_to.toString();
      if (!byMember[memberId]) {
        byMember[memberId] = { completed: 0, velocity: 0 };
      }

      if (item.status === 'completed' || item.status === 'closed' || item.status === 'done') {
        byMember[memberId].completed++;
        byMember[memberId].velocity += item.custom_fields?.story_points || 0;
      }
    });

    return Object.entries(byMember)
      .map(([member, data]) => ({
        member: `Team Member ${member.substring(0, 4)}`,
        completed: data.completed,
        velocity: data.velocity
      }))
      .sort((a, b) => b.velocity - a.velocity)
      .slice(0, 10); // Top 10 performers
  }

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Team performance, velocity trends, and workflow metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Time Range Selector */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          >
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="6m">Last 6 Months</option>
            <option value="1y">Last Year</option>
          </select>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-medium">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Velocity</span>
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.avgVelocity}</div>
          <div className={`flex items-center gap-1 mt-2 text-sm ${stats.velocityTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {stats.velocityTrend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {Math.abs(stats.velocityTrend)}% vs last sprint
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Cycle Time</span>
            <Clock className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.avgCycleTime}</div>
          <div className="text-sm text-gray-500 mt-2">days to complete</div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Completion Rate</span>
            <CheckCircle2 className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.completionRate}%</div>
          <div className="text-sm text-gray-500 mt-2">{stats.totalCompleted} items completed</div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Team Size</span>
            <Users className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.teamSize}</div>
          <div className="text-sm text-gray-500 mt-2">{stats.activeSprints} active sprints</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Velocity Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Sprint Velocity
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={velocityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="sprint" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '0.5rem'
                }}
              />
              <Legend />
              <Bar dataKey="planned" fill="#94a3b8" name="Planned" />
              <Bar dataKey="completed" fill="#3b82f6" name="Completed" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cumulative Flow Diagram */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Cumulative Flow
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={cumulativeFlowData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '0.5rem'
                }}
              />
              <Legend />
              <Area type="monotone" dataKey="done" stackId="1" stroke="#10b981" fill="#10b981" name="Done" />
              <Area type="monotone" dataKey="review" stackId="1" stroke="#8b5cf6" fill="#8b5cf6" name="Review" />
              <Area type="monotone" dataKey="inProgress" stackId="1" stroke="#3b82f6" fill="#3b82f6" name="In Progress" />
              <Area type="monotone" dataKey="todo" stackId="1" stroke="#f59e0b" fill="#f59e0b" name="To Do" />
              <Area type="monotone" dataKey="backlog" stackId="1" stroke="#6b7280" fill="#6b7280" name="Backlog" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Cycle Time by Type */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-600" />
            Cycle Time by Type
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={cycleTimeData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis type="number" stroke="#9ca3af" />
              <YAxis dataKey="itemType" type="category" stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '0.5rem'
                }}
              />
              <Legend />
              <Bar dataKey="avgCycleTime" fill="#8b5cf6" name="Avg Cycle Time (days)" />
              <Bar dataKey="avgLeadTime" fill="#f59e0b" name="Avg Lead Time (days)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Team Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-600" />
            Top Performers
          </h3>
          <div className="space-y-3">
            {teamPerformance.slice(0, 5).map((member, index) => (
              <div key={member.member} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    index === 0 ? 'bg-yellow-100 text-yellow-800' :
                    index === 1 ? 'bg-gray-100 text-gray-800' :
                    index === 2 ? 'bg-orange-100 text-orange-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {index + 1}
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{member.member}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">{member.velocity} pts</div>
                  <div className="text-xs text-gray-500">{member.completed} items</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insights Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Key Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">Velocity Trending {stats.velocityTrend >= 0 ? 'Up' : 'Down'}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Team velocity has {stats.velocityTrend >= 0 ? 'increased' : 'decreased'} by {Math.abs(stats.velocityTrend)}% in the latest sprint
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">Average Cycle Time</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Items are taking an average of {stats.avgCycleTime} days from start to completion
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">Strong Completion Rate</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {stats.completionRate}% of started items have been successfully completed
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
